import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GlassDoor from '../components/GlassDoor';

const logoUrl = 'https://lh3.googleusercontent.com/d/1e3RAmn_wSbG0KSEWap41kkuK-dAkO1vg';
const WIDE_IMG     = 'https://lh3.googleusercontent.com/d/15NNlT9FbFEvxTmiessJn1w2LihOUWP9e';
const PORTRAIT_IMG = 'https://lh3.googleusercontent.com/d/1DX_H0HpVog91Uh6nUIF_0FqX3wzvY1Fw';
const FALLBACK_IMG = 'https://lh3.googleusercontent.com/d/1LKe67RP5clT5FN0AtbbwDGxqOc2p-WIX';

function isTouchDevice() {
  return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
}
function isMobileViewport() {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

type Hotspot = {
  id: string;
  label: string;
  route: string;
  // Desktop coords (% of widescreen image)
  cx: number; cy: number; w: number; h: number;
  // Mobile coords (% of portrait image)
  mcx: number; mcy: number; mw: number; mh: number;
};

// Coordinates placed directly on each object.
// Calibrate with Shift+C or #calibrate in URL.
const HOTSPOTS: Hotspot[] = [
  { id: 'vm', label: 'Visual Merchandising', route: '/visual-merchandising',
    cx: 63, cy: 13, w: 12, h: 12,
    mcx: 52, mcy: 11, mw: 20, mh: 10 },
  { id: 'd3', label: '3-D Design', route: '/3d-design',
    cx: 66, cy: 32, w: 12, h: 9,
    mcx: 58, mcy: 32, mw: 20, mh: 9 },
  { id: 'br', label: 'Branding', route: '/branding',
    cx: 44, cy: 50, w: 10, h: 12,
    mcx: 30, mcy: 50, mw: 16, mh: 12 },
  { id: 'au', label: 'About', route: '/about',
    cx: 69, cy: 69, w: 12, h: 10,
    mcx: 65, mcy: 69, mw: 20, mh: 10 },
];

const CALIBRATE_INIT =
  typeof window !== 'undefined' && window.location.hash.toLowerCase().includes('calibrate');

const Bookshelf: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldShowDoors = !!(location.state as any)?.showDoors;
  const isTouch = useRef(isTouchDevice()).current;
  const isMobile = useRef(isMobileViewport()).current;

  const [showDoor, setShowDoor] = useState(shouldShowDoors);
  const [entered, setEntered] = useState(!shouldShowDoors);
  const [active, setActive] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [readout, setReadout] = useState('click an object…');
  const [cal, setCal] = useState(CALIBRATE_INIT);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.shiftKey && (e.key === 'C' || e.key === 'c')) setCal((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleDoorComplete = useCallback(() => {
    setShowDoor(false);
    setTimeout(() => setEntered(true), 60);
  }, []);

  const handleClick = useCallback((h: Hotspot) => {
    if (cal || leaving) return;
    if (isTouch && active !== h.id) {
      setActive(h.id);
      return;
    }
    setLeaving(true);
    setActive(h.id);
    setTimeout(() => navigate(h.route), 550);
  }, [navigate, cal, isTouch, active, leaving]);

  const handleBgTap = useCallback(() => {
    if (isTouch && active) setActive(null);
  }, [isTouch, active]);

  const onCalibrateClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!cal) return;
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setReadout(`cx: ${x.toFixed(1)}   cy: ${y.toFixed(1)}`);
  }, [cal]);

  return (
    <div
      onClick={handleBgTap}
      style={{
        position: 'fixed', inset: 0, overflow: 'hidden',
        background: '#1a1715',
      }}
    >
      {/* Full-bleed scene image */}
      <div
        onClick={cal ? onCalibrateClick : undefined}
        style={{
          position: 'absolute', inset: 0,
          opacity: leaving ? 0 : entered ? 1 : 0,
          transition: leaving ? 'opacity 0.5s ease' : 'opacity 1.2s ease',
          cursor: cal ? 'crosshair' : 'default',
        }}
      >
        <img
          src={isMobile ? PORTRAIT_IMG : WIDE_IMG}
          alt="RBC Creative — four styled shelves with interactive objects"
          draggable={false}
          onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: isMobile ? 'center 35%' : 'center center',
            display: 'block', userSelect: 'none',
          }}
        />

        {/* Dim overlay when hovering an object */}
        {active && (
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'rgba(10,8,5,0.35)',
            transition: 'opacity 0.4s ease',
          }} />
        )}

        {/* Hotspots — labels on the objects */}
        {HOTSPOTS.map((h) => {
          const on = active === h.id;
          const hcx = isMobile ? h.mcx : h.cx;
          const hcy = isMobile ? h.mcy : h.cy;
          const hw  = isMobile ? h.mw  : h.w;
          const hh  = isMobile ? h.mh  : h.h;

          return (
            <button
              key={h.id}
              onMouseEnter={() => !isTouch && setActive(h.id)}
              onMouseLeave={() => !isTouch && setActive(null)}
              onClick={(e) => { e.stopPropagation(); if (cal) return; handleClick(h); }}
              aria-label={h.label}
              style={{
                position: 'absolute',
                left: `${hcx - hw / 2}%`,
                top:  `${hcy - hh / 2}%`,
                width: `${hw}%`,
                height: `${hh}%`,
                border: cal ? '1px dashed rgba(255,80,80,0.9)' : 'none',
                background: 'transparent',
                cursor: cal ? 'crosshair' : 'pointer',
                padding: 0, zIndex: 5,
                WebkitTapHighlightColor: 'transparent',
                animation: entered ? `fadeInObject 0.7s ease 0.3s both` : 'none',
              }}
            >
              {/* Bright cutout — keeps this object visible when the rest dims */}
              {on && (
                <span style={{
                  position: 'absolute', inset: '-20%',
                  borderRadius: '50%',
                  background: 'radial-gradient(ellipse at center, rgba(255,248,230,0.22) 0%, transparent 70%)',
                  pointerEvents: 'none',
                }} />
              )}

              {/* Label — centered on the object */}
              <span style={{
                position: 'absolute',
                left: '50%', top: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center', pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}>
                <span style={{
                  fontFamily: '"Playfair Display", serif',
                  fontStyle: 'italic',
                  fontSize: isMobile ? 'clamp(13px, 3.8vw, 18px)' : 'clamp(14px, 1.6vw, 20px)',
                  lineHeight: 1,
                  letterSpacing: '0.04em',
                  color: on ? '#fff' : 'rgba(255,252,245,0.88)',
                  textShadow: on
                    ? '0 2px 24px rgba(0,0,0,1), 0 0 60px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,1)'
                    : '0 2px 16px rgba(0,0,0,0.95), 0 0 40px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.9)',
                  transition: 'color 0.3s ease, text-shadow 0.3s ease',
                }}>
                  {cal ? `${h.label} (${hcx},${hcy})` : h.label}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Calibration readout */}
      {cal && (
        <div style={{
          position: 'absolute', top: 16, right: 16, zIndex: 40,
          fontFamily: 'monospace', fontSize: 13, color: '#fff',
          background: 'rgba(0,0,0,0.75)', padding: '10px 14px', borderRadius: 6,
          pointerEvents: 'none', whiteSpace: 'pre-line',
        }}>
          CALIBRATE  (Shift+C to exit){'\n'}{readout}
        </div>
      )}

      {/* Logo */}
      {!showDoor && (
        <button
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: isMobile ? 14 : 'clamp(16px,2.5vh,26px)',
            left: isMobile ? 14 : 'clamp(16px,2.5vw,26px)',
            zIndex: 30,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            opacity: 0.5, transition: 'opacity 0.3s',
            animation: 'fadeInPage 0.8s ease forwards',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
        >
          <img src={logoUrl} alt="RBC Creative" style={{
            height: isMobile ? 22 : 'clamp(28px,4vw,42px)', width: 'auto',
          }} />
        </button>
      )}

      {/* Bottom hint */}
      {!showDoor && (
        <p style={{
          position: 'absolute', bottom: isMobile ? 14 : 'clamp(14px,2.5vh,24px)',
          left: 0, right: 0, textAlign: 'center',
          zIndex: 30, margin: 0, pointerEvents: 'none',
          fontFamily: '"Lato", sans-serif',
          fontSize: isMobile ? 9 : 'clamp(7px,0.8vw,10px)',
          fontWeight: 300,
          letterSpacing: '0.35em',
          color: 'rgba(245,238,225,0.5)',
          textShadow: '0 1px 10px rgba(0,0,0,0.8)',
          animation: 'fadeInPage 1s ease 0.5s both',
        }}>
          {isTouch ? 'TAP AN OBJECT TO EXPLORE' : 'HOVER AN OBJECT · CLICK TO ENTER'}
        </p>
      )}

      {showDoor && <GlassDoor onComplete={handleDoorComplete} />}
    </div>
  );
};

export default Bookshelf;
